import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { OrganizationSharedFieldErrors, OrganizationSharedFields } from '@/lib/organization-form';
import { formatNanpPhone } from '@/lib/validation/phone';
import { getOrganizationRegionLabel } from '@/lib/organization-form';

type OrganizationFormFieldsProps = {
  values: OrganizationSharedFields;
  errors?: OrganizationSharedFieldErrors;
  disabled?: boolean;
  showName?: boolean;
  showAddress?: boolean;
  addressHeading?: string;
  onChange: (patch: Partial<OrganizationSharedFields>) => void;
};

export function OrganizationFormFields({
  values,
  errors,
  disabled = false,
  showName = true,
  showAddress = true,
  addressHeading = 'Organization address',
  onChange,
}: OrganizationFormFieldsProps) {
  return (
    <>
      {showName ? (
        <div className="space-y-2">
          <Label>Name</Label>
          <Input value={values.name} onChange={(e) => onChange({ name: e.target.value })} disabled={disabled} />
          {errors?.name ? <p className="text-destructive text-xs">{errors.name}</p> : null}
        </div>
      ) : null}

      <div className="space-y-2">
        <Label>Website</Label>
        <Input
          value={values.website}
          onChange={(e) => onChange({ website: e.target.value })}
          placeholder="https://example.com"
          autoComplete="url"
          disabled={disabled}
        />
        {errors?.website ? <p className="text-destructive text-xs">{errors.website}</p> : null}
      </div>

      <div className="space-y-2">
        <Label>Email</Label>
        <Input
          type="email"
          value={values.email}
          onChange={(e) => onChange({ email: e.target.value })}
          placeholder="org@example.com"
          autoComplete="email"
          disabled={disabled}
        />
        {errors?.email ? <p className="text-destructive text-xs">{errors.email}</p> : null}
      </div>

      <div className="space-y-2">
        <Label>Phone</Label>
        <div className="relative">
          <span className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2 text-sm">+1</span>
          <Input
            className="pl-10"
            placeholder="(000) 000-0000"
            value={values.phone}
            onChange={(e) => onChange({ phone: formatNanpPhone(e.target.value) })}
            inputMode="tel"
            autoComplete="tel"
            disabled={disabled}
          />
        </div>
      </div>

      {showAddress ? (
        <div className="space-y-4 rounded-md border p-4 md:col-span-2">
          <p className="text-sm font-medium">{addressHeading}</p>

          <div className="space-y-2">
            <Label htmlFor="orgAddressLine1">Address Line 1</Label>
            <Input
              id="orgAddressLine1"
              value={values.addressLine1}
              onChange={(e) => onChange({ addressLine1: e.target.value })}
              placeholder="123 Main St"
              autoComplete="address-line1"
              disabled={disabled}
            />
            {errors?.addressLine1 ? <p className="text-destructive text-xs">{errors.addressLine1}</p> : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="orgAddressLine2">Address Line 2 (Optional)</Label>
            <Input
              id="orgAddressLine2"
              value={values.addressLine2}
              onChange={(e) => onChange({ addressLine2: e.target.value })}
              placeholder="Suite 400"
              autoComplete="address-line2"
              disabled={disabled}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="orgCity">City</Label>
              <Input
                id="orgCity"
                value={values.city}
                onChange={(e) => onChange({ city: e.target.value })}
                autoComplete="address-level2"
                disabled={disabled}
              />
              {errors?.city ? <p className="text-destructive text-xs">{errors.city}</p> : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="orgCountry">Country</Label>
              <Select
                value={values.country}
                onValueChange={(value) => onChange({ country: value as OrganizationSharedFields['country'] })}
                disabled={disabled}
              >
                <SelectTrigger id="orgCountry">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CA">Canada</SelectItem>
                  <SelectItem value="US">United States</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="orgRegion">{getOrganizationRegionLabel(values.country)}</Label>
              <Input
                id="orgRegion"
                value={values.region}
                onChange={(e) => onChange({ region: e.target.value })}
                autoComplete="address-level1"
                disabled={disabled}
              />
              {errors?.region ? <p className="text-destructive text-xs">{errors.region}</p> : null}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="orgPostalCode">Postal / ZIP</Label>
            <Input
              id="orgPostalCode"
              value={values.postalCode}
              onChange={(e) => onChange({ postalCode: e.target.value })}
              autoComplete="postal-code"
              disabled={disabled}
            />
            {errors?.postalCode ? <p className="text-destructive text-xs">{errors.postalCode}</p> : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
